import { Controller } from '@hotwired/stimulus';

// The phone app offered on the page (docs/NOTIFY.md § The app nudge): the
// home's modal (fire/shared/_app_install, a <dialog>) and every other page's floating bar
// (fire/shared/_app_nudge) are this one controller. The server already said
// yes to everything it can know: a live store, an account older than today,
// not the shell, a phone for the bar. What is left is this device's: never for
// 30 days after the close or Instalar (localStorage), and for the bar once a
// visit (sessionStorage). Storage off, a private window: it shows, the
// harmless way to be wrong.
//
//   data-nudge-once-value  true: shown once a visit (the bar)
//   data-nudge-float-value true: the bar. It rests just above the host's dock
//                          (a .dock on the page, and whatever is raised out
//                          of it), else above the bottom edge, and lifts the
//                          help bubble (#fire-help-bubble) and any fab clear of itself.
const QUIET = 30 * 24 * 60 * 60 * 1000;
const GAP = 8;
const CORNER = '#fire-help-bubble, .fab';

const read = (store, key) => {
  try {
    return window[store].getItem(key);
  } catch {
    return null;
  }
};

const write = (store, key, value) => {
  try {
    window[store].setItem(key, value);
  } catch {
    /* no storage, no memory */
  }
};

export default class extends Controller {
  static values = { once: Boolean, float: Boolean };

  connect() {
    const no = Number(read('localStorage', 'app-nudge-no'));
    const quiet = no > Date.now() - QUIET;
    const seen = this.onceValue && read('sessionStorage', 'app-nudge-seen');
    const show = !quiet && !seen;
    if (show && this.onceValue) write('sessionStorage', 'app-nudge-seen', '1');
    if (this.dialog) {
      if (show && !this.element.open) this.element.showModal();
    } else {
      this.element.hidden = !show;
    }
    if (show && this.floatValue) {
      this.place = () => this.rest();
      window.addEventListener('resize', this.place);
      this.rest();
    }
  }

  disconnect() {
    if (this.place) window.removeEventListener('resize', this.place);
    this.lower();
  }

  get dialog() {
    return this.element instanceof HTMLDialogElement;
  }

  // Instalar and a store's button are links: this runs and the store still
  // opens. The modal's x, backdrop and Escape all end in its `close` event.
  later() {
    write('localStorage', 'app-nudge-no', String(Date.now()));
    if (this.dialog) {
      if (this.element.open) this.element.close();
    } else {
      this.element.hidden = true;
    }
    this.lower();
  }

  // Where the bar stands: above the dock's highest point, else the CSS default.
  rest() {
    const dock = document.querySelector('.dock');
    const rect = dock && dock.getBoundingClientRect();
    if (rect && rect.height > 0) {
      const top = [dock, ...dock.querySelectorAll('*')].reduce((high, node) => {
        const box = node.getBoundingClientRect();
        return box.height > 0 ? Math.min(high, box.top) : high;
      }, rect.top);
      this.element.style.setProperty('--nudge-bottom', `${window.innerHeight - top + GAP}px`);
    } else {
      this.element.style.removeProperty('--nudge-bottom');
    }
    this.lift();
  }

  // What floats in the corner (the help bubble, a daisy fab: kio's radio) rises
  // by exactly what the bar covers of it, and no more.
  lift() {
    const barTop = window.innerHeight - parseFloat(getComputedStyle(this.element).bottom) - this.element.offsetHeight;
    this.corner().forEach((node) => {
      // Measured where it rests, not mid-glide from where it was.
      node.style.transition = 'none';
      node.style.translate = '';
      const cover = node.getBoundingClientRect().bottom - barTop + GAP;
      if (cover > 0) node.style.translate = `0 ${-cover}px`;
      node.getBoundingClientRect(); // flush, then let it glide
      node.style.transition = '';
    });
  }

  lower() {
    this.corner().forEach((node) => {
      node.style.translate = '';
    });
  }

  corner() {
    return [...document.querySelectorAll(CORNER)];
  }
}
